import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, DollarSign, Home, Calendar, Send, Loader2, ArrowRight, Zap } from "lucide-react";

/* Public deal view — no auth required. Buyer opens this from an email blast. */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

export default function PublicDealPage() {
  const { code } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiry, setInquiry] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/wholesale/deals/public/${code}`)
      .then((r) => setDeal(r.data))
      .catch(() => setDeal(false))
      .finally(() => setLoading(false));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!inquiry.name || (!inquiry.email && !inquiry.phone)) {
      toast.error("Enter your name and either email or phone");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/wholesale/deals/public/${code}/inquire`, inquiry);
      setSubmitted(true);
      toast.success("Inquiry sent");
    } catch { toast.error("Failed to send"); } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#14100c] flex items-center justify-center"><Loader2 className="animate-spin text-[#d4a574]" size={32} /></div>;
  if (deal === false) return (
    <div className="min-h-screen bg-[#14100c] text-[#f4ede2] flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="font-display text-3xl font-bold mb-2">Deal not found</div>
        <div className="text-[13px] text-[#b8ac96] mb-6">This deal link may have expired or been removed.</div>
        <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px]">Visit The Close Out <ArrowRight size={14} /></Link>
      </div>
    </div>
  );

  const spread = (Number(deal.arv || 0) - Number(deal.asking_price || 0) - Number(deal.repair_estimate || 0));

  return (
    <div className="min-h-screen bg-[#14100c] text-[#f4ede2]" data-testid="public-deal-page">
      {/* Header */}
      <header className="border-b border-[#33291f] py-4 px-8">
        <Link to="/" className="flex items-center gap-2.5 max-w-max">
          <div className="w-9 h-9 rounded bg-gradient-to-br from-[#d4a574] to-[#a67b4c] flex items-center justify-center text-[#14100c] font-black font-display">C</div>
          <div>
            <div className="font-display font-bold text-[16px] tracking-tight">The Close Out</div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#7a6d5a]">Assignable Deal</div>
          </div>
        </Link>
      </header>

      <div className="max-w-5xl mx-auto px-8 py-10 grid lg:grid-cols-3 gap-6">
        {/* Left column — deal details */}
        <div className="lg:col-span-2 space-y-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold mb-2 flex items-center gap-2">
              <Zap size={12} /> Assignable Contract
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold" data-testid="deal-address">{deal.property_address}</h1>
            <div className="text-[14px] text-[#b8ac96] mt-1 flex items-center gap-1.5">
              <MapPin size={13} /> {deal.city}, {deal.state} {deal.zip}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="ARV" value={fmt(deal.arv)} color="#34d399" testid="deal-arv" />
            <Stat label="Your Price" value={fmt(deal.asking_price)} color="#d4a574" testid="deal-asking" />
            <Stat label="Repair Est." value={fmt(deal.repair_estimate)} />
            <Stat label="Est. Spread" value={fmt(spread)} color={spread > 0 ? "#34d399" : "#f87171"} testid="deal-spread" />
          </div>

          <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
            <div className="font-display text-lg font-semibold mb-3">Property Details</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[13px]">
              <div><div className="text-[10px] uppercase text-[#7a6d5a]">Type</div><div className="font-semibold">{deal.property_type || "—"}</div></div>
              <div><div className="text-[10px] uppercase text-[#7a6d5a]">Beds</div><div className="font-semibold">{deal.beds || "—"}</div></div>
              <div><div className="text-[10px] uppercase text-[#7a6d5a]">Baths</div><div className="font-semibold">{deal.baths || "—"}</div></div>
              <div><div className="text-[10px] uppercase text-[#7a6d5a]">Sqft</div><div className="font-semibold">{deal.sqft || "—"}</div></div>
            </div>
          </div>

          {deal.highlights && (
            <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
              <div className="font-display text-lg font-semibold mb-3">Why this is a deal</div>
              <div className="text-[13px] text-[#f4ede2] whitespace-pre-line leading-relaxed">{deal.highlights}</div>
            </div>
          )}

          <div className="border border-[#33291f] rounded-lg bg-[#1c1712] p-5">
            <div className="font-display text-lg font-semibold mb-3">Terms</div>
            <div className="text-[13px] text-[#b8ac96] space-y-2">
              <div className="flex justify-between"><span>Closing on or before</span><span className="font-mono text-[#f4ede2]">{deal.closing_date || "Flexible"}</span></div>
              <div className="flex justify-between"><span>Assignment fee</span><span className="font-mono text-[#d4a574]">{fmt(deal.assignment_fee)}</span></div>
              <div className="flex justify-between"><span>Deal status</span><span className="uppercase text-[10px] tracking-wider text-[#34d399]">{deal.status}</span></div>
            </div>
          </div>
        </div>

        {/* Right column — Contact wholesaler */}
        <div className="lg:col-span-1">
          <div className="border border-[#d4a574]/40 rounded-lg bg-gradient-to-br from-[#241d16] to-[#1c1712] p-5 sticky top-6">
            {submitted ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-[#34d399]/10 border border-[#34d399] flex items-center justify-center mx-auto mb-3">
                  <Send size={20} className="text-[#34d399]" />
                </div>
                <div className="font-display text-lg font-semibold">Inquiry sent</div>
                <div className="text-[12px] text-[#b8ac96] mt-2">{deal.wholesaler_name} will reach out shortly.</div>
              </div>
            ) : (
              <>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4a574] font-semibold mb-2">Interested?</div>
                <div className="font-display text-lg font-semibold mb-1">Contact {deal.wholesaler_name}</div>
                {deal.wholesaler_company && <div className="text-[11px] text-[#7a6d5a] mb-4">{deal.wholesaler_company}</div>}
                <form onSubmit={submit} className="space-y-2" data-testid="inquiry-form">
                  <input required placeholder="Your name" value={inquiry.name} onChange={(e) => setInquiry({ ...inquiry, name: e.target.value })} data-testid="inq-name" className="w-full bg-[#14100c] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
                  <input type="email" placeholder="Email" value={inquiry.email} onChange={(e) => setInquiry({ ...inquiry, email: e.target.value })} data-testid="inq-email" className="w-full bg-[#14100c] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
                  <input placeholder="Phone" value={inquiry.phone} onChange={(e) => setInquiry({ ...inquiry, phone: e.target.value })} className="w-full bg-[#14100c] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
                  <textarea rows={3} placeholder="I'm interested. Can we talk?" value={inquiry.message} onChange={(e) => setInquiry({ ...inquiry, message: e.target.value })} className="w-full bg-[#14100c] border border-[#33291f] rounded px-3 py-2 text-[13px] focus:outline-none focus:border-[#d4a574]" />
                  <button type="submit" disabled={submitting} data-testid="inq-submit" className="w-full py-2.5 rounded bg-[#d4a574] text-[#14100c] font-semibold text-[13px] hover:bg-[#e8b988] disabled:opacity-60 flex items-center justify-center gap-2">
                    {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send Inquiry
                  </button>
                </form>
                <div className="text-[10px] font-mono text-[#7a6d5a] mt-3 text-center">Response usually within 24 hrs</div>
              </>
            )}
          </div>
        </div>
      </div>

      <footer className="border-t border-[#33291f] py-6 text-center text-[11px] text-[#7a6d5a]">
        Powered by <Link to="/" className="text-[#d4a574] hover:underline">The Close Out</Link> · Distress Intelligence Platform
      </footer>
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div className="p-3 rounded border border-[#33291f] bg-[#1c1712]" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#7a6d5a] mb-1">{label}</div>
      <div className="font-display text-xl font-bold font-mono" style={{ color: color || "#f4ede2" }}>{value}</div>
    </div>
  );
}
